import { LitElement, html, css } from 'lit';
import { customElement } from 'lit/decorators.js';
import { styles as sharedStyles } from '../../styles/shared-styles';
import '@shoelace-style/shoelace/dist/components/card/card.js';
import { writeMdTemplate } from '../../utility/markdown';

// Public privacy policy. Anonymous-accessible — Google's OAuth
// consent screen review crawler needs to reach it without signing
// in. Linked from the login page footer + the About page.
//
// The current text was drafted to be plain-English and to cover the
// categories Google looks for (collection / use / sharing /
// retention / contact). It is NOT a substitute for legal review;
// the user has been told to have counsel review before relying on
// it for liability.
@customElement('app-privacy')
export class AppPrivacy extends LitElement {
  static styles = [
    sharedStyles,
    css`
      :host {
        display: block;
        max-width: 760px;
        margin: 0 auto;
        padding: 24px 20px 48px;
        box-sizing: border-box;
      }
      sl-card {
        display: block;
      }
      h1,
      h2 {
        font-family: var(--font-display, system-ui);
      }
      h1 {
        margin-top: 0;
      }
      h2 {
        margin-top: 1.8rem;
      }
      .meta {
        margin-bottom: 1.5rem;
        font-size: 0.85rem;
        color: var(--sl-color-neutral-600);
      }
    `,
  ];

  render() {
    const markdown = writeMdTemplate`${'ref:privacy:body'}
    # Privacy Policy

    **Last updated: 2026-05-23**

    This page explains what information Pag-asenso collects, why we collect it, how we use it, and the choices you have.

    ## Who runs this service

    Pag-asenso is a point-of-sale application operated by its developer team. If a single contact is required for legal or compliance purposes, please email **privacy@pag-asenso.com**.

    ## What we collect

    We collect only what is necessary to run the service:

    - **Account details** you provide when you register or sign in: email address, display name, and (when applicable) the profile picture provided by Google or Facebook on sign-in.
    - **Shop and business data** you enter into the app: menu items, prices, stock, suppliers, sales, payments, stocktakes, and the operational records the app produces as you use it.
    - **Operational telemetry** the app generates as you use it: login activity, device identifiers (used to bind a device to a specific shop on shared tablets), and basic audit trails for state-changing operations.

    We do not collect financial card data ourselves. When a sale is paid by card we record only the operator-entered amount and a label (e.g. "card"). Card processing, if any, is performed by your own payment terminal or merchant provider; we do not see, store, or have access to card numbers, CVVs, or bank credentials.

    ## How we use it

    - To operate the service: authenticate you, render your shop's data back to you and your staff, generate reports, propagate realtime updates between your devices.
    - To maintain service quality: diagnose errors, investigate abuse, and improve reliability.
    - To send service-related messages such as account confirmation, security alerts, and important account notices.

    We do **not** use your shop's transactional data to train machine-learning models. We do **not** use it for advertising. We do not sell, rent, or lend your data to third parties.

    ## Who can access your data

    Only authorised members of the Pag-asenso operations team. Access is limited to what is necessary for support, diagnostics, or compliance with a lawful request, and team members are bound to treat your data with full confidence. Access is logged.

    Within your account, the staff you yourself invite to your shop (with the role you grant them) can see the shop's operational data the role permits. You control these invitations.

    ## Third-party services we rely on

    - **Microsoft Azure** — application hosting, blob/table storage, realtime messaging. Your data is stored in Azure data centres in the region we operate in. Azure's privacy practices apply to that storage layer.
    - **Google Sign-in** and **Facebook Login** — optional sign-in providers. When you sign in via either, that provider sees the fact of the sign-in and we receive the email + display name they choose to share. Their own privacy policies apply to that handoff.

    No other third-party services receive your shop data.

    ## Data retention

    - While your subscription is active, your data is retained so the app works as expected.
    - If your subscription lapses, **we do not guarantee retention of your data after a 30-day grace period.** Data may be deleted at any time after that window without further notice.
    - Account-level audit records may be retained longer than shop data where required for security investigations or legal compliance.

    ## Data export

    You may request a one-time export of your shop's data in JSON format. A **per-download service fee** applies (current rates are quoted on request; charged for the cost of building and delivering the archive). Contact **privacy@pag-asenso.com** to request an export.

    ## Cookies and local storage

    The app uses browser **local storage** to remember your sign-in session, your last selected shop, and offline-resilience data such as queued cart entries. We do not use third-party analytics or advertising cookies.

    ## Children

    Pag-asenso is for business operators. We do not knowingly collect personal data from children under 13. If you believe a child has signed up, contact us and we will delete the account.

    ## Your rights

    Subject to applicable law, you may request:

    - A copy of your personal data we hold.
    - Correction of inaccurate personal data.
    - Deletion of your account and associated data (where we are not required to retain it).
    - Withdrawal of consent for any optional processing.

    Send requests to **privacy@pag-asenso.com**. We aim to respond within 30 days.

    ## Security

    We use industry-standard practices: transport encryption (HTTPS), encryption at rest for stored data, password hashing for any locally-issued passwords, rate-limited sign-in, optional device-binding, and least-privilege team access. No system is perfectly secure; please use strong passwords and enable two-factor authentication where available.

    ## Changes to this policy

    We may update this policy. Material changes will be announced via in-app notice or email. The "Last updated" date at the top reflects the latest revision.

    ## Contact

    Email **privacy@pag-asenso.com** for any privacy-related question or request.
    `;

    return html`
      <sl-card>
        <div class="meta">
          You can find the matching Terms of Service at
          <a href="/terms">/terms</a>.
        </div>
        ${markdown}
      </sl-card>
    `;
  }
}
